/** Shared desktop/mobile navigation. Keep primary work one click away and account tools together. */

import {
	ClipboardCheck,
	CreditCard,
	Eye,
	FolderKanban,
	KeyRound,
	LayoutDashboard,
	type LucideIcon,
	Plug,
	Puzzle,
	RefreshCw,
	Settings,
	Shield,
	ShieldCheck,
	ShieldHalf,
	User,
	Wrench,
} from 'lucide-react'
import { HIDE_SYNC } from './feature-flags'

/** A leaf nav entry — renders as a link. */
export interface NavItem {
	href: string
	label: string
	icon: LucideIcon
	match: (pathname: string) => boolean
	/**
	 * Which live counter decorates this entry, if any. Declarative rather than
	 * an `href === '/review'` check at the render site so a group header can
	 * roll the same counter up when it is collapsed.
	 */
	badge?: NavBadgeKind
}

/** The live counters a nav entry can carry. */
export type NavBadgeKind = 'pendingReview' | 'pendingApprovals'

/** Current value of every counter; 0 means "no badge". */
export type NavBadgeCounts = Record<NavBadgeKind, number>

/** A branch nav entry — renders as a collapsible section owning NavItems. */
export interface NavGroup {
	/** Stable across releases: it is the localStorage key for the open/closed state. */
	id: string
	label: string
	icon: LucideIcon
	items: NavItem[]
}

export type NavEntry = NavItem | NavGroup

export function isNavGroup(entry: NavEntry): entry is NavGroup {
	return 'items' in entry
}

const DASHBOARD_ITEM: NavItem = {
	href: '/',
	label: 'Dashboard',
	icon: LayoutDashboard,
	match: (p) => p === '/',
}
const REVIEW_ITEM: NavItem = {
	href: '/review',
	label: 'Review',
	icon: ShieldCheck,
	match: (p) => p === '/review' || p.startsWith('/extensions/'),
	badge: 'pendingReview',
}
// Changes an agent proposed through the connector. Top-level next to Review:
// both are "something waits for your decision", and the agent's own link is
// otherwise the only way in.
const APPROVALS_ITEM: NavItem = {
	href: '/approvals',
	label: 'Approvals',
	icon: ClipboardCheck,
	match: (p) => p === '/approvals' || p.startsWith('/approvals/'),
	badge: 'pendingApprovals',
}
// @extenshi/guard — usage + inventory-credit purchase. The inventory LIST it
// produces lives on the Extensions entry below; this one deliberately links
// there instead of rendering a second copy. That direct dependency is why the
// two share the Protection group.
const GUARD_ITEM: NavItem = {
	href: '/guard',
	label: 'Guard',
	icon: ShieldHalf,
	match: (p) => p.startsWith('/guard'),
}
// The user's own installed-extension inventory (shared via `guard scan`).
// Distinct route from the developer `/extensions/*` review pages above.
const EXTENSIONS_ITEM: NavItem = {
	href: '/installed-extensions',
	label: 'Extensions',
	icon: Puzzle,
	match: (p) => p === '/installed-extensions',
}
// Delisting Watch (iteration 3 · 3a). Lives in Protection rather than
// Development: it answers "is my extension still live", which is the same
// question Guard and Extensions answer — not something you build with.
const MONITORING_ITEM: NavItem = {
	href: '/monitoring',
	label: 'Monitoring',
	icon: Eye,
	match: (p) => p.startsWith('/monitoring'),
}
const SYNC_ITEM: NavItem = {
	href: '/sync',
	label: 'Sync',
	icon: RefreshCw,
	match: (p) => p === '/sync',
}
const PAYMENTS_ITEM: NavItem = {
	href: '/payments',
	label: 'Payments',
	icon: CreditCard,
	match: (p) => p === '/payments' || p.startsWith('/payments/'),
}
const PROJECTS_ITEM: NavItem = {
	href: '/projects',
	label: 'Projects',
	icon: FolderKanban,
	match: (p) => p.startsWith('/projects'),
}
const TOOLS_ITEM: NavItem = {
	href: '/tools',
	label: 'Tools',
	icon: Wrench,
	match: (p) => p.startsWith('/tools'),
}
const API_KEYS_ITEM: NavItem = {
	href: '/api-keys',
	label: 'API key',
	icon: KeyRound,
	match: (p) => p === '/api-keys',
}
const INTEGRATIONS_ITEM: NavItem = {
	href: '/integrations',
	label: 'Integrations',
	icon: Plug,
	match: (p) => p.startsWith('/integrations'),
}
const BILLING_ITEM: NavItem = {
	href: '/billing',
	label: 'Billing',
	icon: CreditCard,
	match: (p) => p.startsWith('/billing'),
}
const PROFILE_ITEM: NavItem = {
	href: '/profile',
	label: 'Profile',
	icon: User,
	match: (p) => p === '/profile',
}

/** Legacy persisted key retained for old navigation preferences. */
export const DEVELOPMENT_GROUP_ID = 'development'
export const PROTECTION_GROUP_ID = 'protection'
export const SETTINGS_GROUP_ID = 'settings'

/**
 * The full two-level nav. Projects and Integrations are always reachable.
 *
 * Sync uses the compile-time HIDE_SYNC constant until cross-browser sync ships.
 */
export function buildNavEntries(): NavEntry[] {
	const protection: NavGroup = {
		id: PROTECTION_GROUP_ID,
		label: 'Protection',
		icon: Shield,
		items: [GUARD_ITEM, MONITORING_ITEM, EXTENSIONS_ITEM, ...(HIDE_SYNC ? [] : [SYNC_ITEM])],
	}
	return [
		DASHBOARD_ITEM,
		PROJECTS_ITEM,
		PAYMENTS_ITEM,
		REVIEW_ITEM,
		APPROVALS_ITEM,
		TOOLS_ITEM,
		protection,
		{
			id: SETTINGS_GROUP_ID,
			label: 'Settings',
			icon: Settings,
			items: [INTEGRATIONS_ITEM, API_KEYS_ITEM, BILLING_ITEM, PROFILE_ITEM],
		},
	]
}

/** The count a given entry's badge should show, or 0 for "no badge". */
export function badgeCount(item: NavItem, counts: NavBadgeCounts): number {
	return item.badge ? counts[item.badge] : 0
}

/** What a screen reader hears for an entry's badge. */
export function badgeLabel(kind: NavBadgeKind, count: number): string {
	return kind === 'pendingApprovals'
		? `${count} ${count === 1 ? 'approval' : 'approvals'} waiting for your decision`
		: `${count} findings waiting for your review`
}

/**
 * The collapsed-group rollup: the summed count, plus a label naming each
 * counter that contributes, so a group holding two kinds never announces
 * one total under the wrong noun.
 */
export function groupBadge(group: NavGroup, counts: NavBadgeCounts): { count: number; label: string } {
	const parts: string[] = []
	let count = 0
	for (const item of group.items) {
		const n = badgeCount(item, counts)
		if (n === 0 || !item.badge) continue
		count += n
		parts.push(badgeLabel(item.badge, n))
	}
	return { count, label: parts.join(', ') }
}

/** Every reachable leaf, groups flattened away — for link-level assertions. */
export function flattenNavEntries(entries: NavEntry[]): NavItem[] {
	return entries.flatMap((entry) => (isNavGroup(entry) ? entry.items : [entry]))
}

/**
 * The group owning the current route, or null when the active entry is
 * top-level. Drives "a group may never hide the page you are on".
 */
export function findActiveGroupId(entries: NavEntry[], pathname: string): string | null {
	for (const entry of entries) {
		if (isNavGroup(entry) && entry.items.some((item) => item.match(pathname))) return entry.id
	}
	return null
}

// ─── Group open/closed state ────────────────────────────────────────────
//
// Kept here, as pure functions over a plain object, so the invariants below are
// testable without a DOM — useNavGroupState() in components/layout/nav-config
// is only the React shell that wires them to localStorage and effects.

/**
 * Which groups are CLOSED (`true` = collapsed). Storing the closed set rather
 * than the open one makes an unknown id default to OPEN, so a group added in a
 * later release is visible to existing users instead of arriving pre-hidden,
 * and the pre-hydration render (empty object) matches SSR.
 */
export type NavGroupState = Record<string, boolean>

/**
 * Read the persisted state, tolerating anything the key might actually hold.
 *
 * localStorage is shared, user-writable and survives across releases, so the
 * value can be absent, truncated JSON, or a valid JSON value of the wrong shape
 * — `null` and `"[]"` both parse fine yet would make `state[id]` throw or lie.
 * Everything unusable degrades to "no groups closed", never to a crash that
 * would take the whole sidebar down.
 */
export function parseNavGroupState(raw: string | null): NavGroupState {
	if (!raw) return {}
	let parsed: unknown
	try {
		parsed = JSON.parse(raw)
	} catch {
		return {}
	}
	if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
	const state: NavGroupState = {}
	for (const [id, closed] of Object.entries(parsed)) {
		if (closed === true) state[id] = true
	}
	return state
}

export function isGroupOpen(state: NavGroupState, id: string): boolean {
	return !state[id]
}

/**
 * Open is represented by the ABSENCE of the key, never by `key: false`.
 *
 * Keeping one representation means the in-memory state and the state read back
 * after a reload are identical (parseNavGroupState drops non-`true` entries), so
 * anything that later inspects the object — `Object.keys(state).length` to count
 * collapsed groups, a diff, a snapshot test — cannot disagree with itself.
 */
export function toggleGroup(state: NavGroupState, id: string): NavGroupState {
	if (!state[id]) return { ...state, [id]: true }
	const { [id]: _closed, ...open } = state
	return open
}

/**
 * Force a group open — the "a group may never hide the page you are on" rule.
 *
 * Returns the SAME object when it is already open. That identity is load-bearing,
 * not an optimisation: the caller applies this from a React effect, and a fresh
 * object every run would set state on every render and spin.
 */
export function openGroup(state: NavGroupState, id: string): NavGroupState {
	if (!state[id]) return state
	const { [id]: _closed, ...open } = state
	return open
}
